import { CommonModule, formatCurrency } from '@angular/common';
import { Component, inject, input, signal, type InputSignal, type TemplateRef, type WritableSignal } from '@angular/core';
import { UIChart } from 'primeng/chart';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CardHeader } from '../../../../shared/components/card/card-header/card-header';
import { Card } from '../../../../shared/components/card/card/card';
import { EmptyChart } from '../../../../shared/components/empty-chart/empty-chart';
import type { ChartConfiguration } from 'chart.js';
import { barChartDefaultOptions, chartColors, doughnutChartDefaultOptions } from '../../../../interfaces/charts';
import type { DashboardType, FinancialDepositAndWithdrawChart, FinancialDepositAndWithdrawMetrics, FinancialExpensesByCategory, FinancialInvoice, FinancialProfitAndExpenses } from '../../../../interfaces/dashboard';
import { DashboardService } from '../../../../services/dashboard.service';
import { SocketService } from '../../../../services/socket.service';
import { type AnimationOptions } from 'ngx-lottie';
import { SkeletonModule } from 'primeng/skeleton';
import { SimpleHorizontalBar } from "../../../../shared/components/simple-horizontal-bar/simple-horizontal-bar";
import { finalize } from 'rxjs';
import { DialogModule } from 'primeng/dialog';
import dayjs from 'dayjs';
import { DatePickerModule } from 'primeng/datepicker';
import { DividerModule } from 'primeng/divider';

@Component({
  selector: 'app-dashboard-financial',
  imports: [
    CommonModule,
    Card,
    CardHeader,
    ProgressSpinnerModule,
    UIChart,
    EmptyChart,
    SkeletonModule,
    SimpleHorizontalBar,
    DialogModule,
    DatePickerModule,
    DividerModule
],
  templateUrl: './dashboard-financial.html',
  styleUrl: './dashboard-financial.css',
})
export class DashboardFinancial {
  private readonly _dashboardService: DashboardService = inject(DashboardService);
  private readonly _socketService: SocketService = inject(SocketService);

  public startDateFilter: InputSignal<Date|null> = input<Date|null>(null);
  public endDateFilter: InputSignal<Date|null> = input<Date|null>(null);

  public dialogVisible: boolean = false;
  public dialogTitle: WritableSignal<string> = signal<string>('');
  public selectedDialogChart: WritableSignal<TemplateRef<any>|null> = signal<TemplateRef<any>|null>(null);

  public emptyLottieOptions: AnimationOptions = {
    path: 'assets/lottie/empty.json',
    loop: true,
    autoplay: true,
  };

  public financialInOutLoading: WritableSignal<boolean> = signal<boolean>(false);
  public financialInOutData: WritableSignal<FinancialDepositAndWithdrawChart|null> = signal<FinancialDepositAndWithdrawChart|null>(null);
  public financialInOutChartData: WritableSignal<any> = signal<any>(null);
  public financialInOutOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...barChartDefaultOptions});

  public financialDepositAndWithdrawMetricsLoading: WritableSignal<boolean> = signal<boolean>(false);
  public financialDepositAndWithdrawMetricsData: WritableSignal<FinancialDepositAndWithdrawMetrics|null> = signal<FinancialDepositAndWithdrawMetrics|null>(null);

  public financialByCategoryLoading: WritableSignal<boolean> = signal<boolean>(false);
  public financialByCategoryData: WritableSignal<FinancialExpensesByCategory|null> = signal<FinancialExpensesByCategory|null>(null);
  public financialByCategoryChartData: WritableSignal<any> = signal<any>(null);
  public financialByCategoryOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...doughnutChartDefaultOptions});

  public financialProfitExpensesLoading: WritableSignal<boolean> = signal<boolean>(false);
  public financialProfitExpensesData: WritableSignal<FinancialProfitAndExpenses|null> = signal<FinancialProfitAndExpenses|null>(null);

  public financialProfitChartData: WritableSignal<any> = signal<any>(null);
  public financialProfitOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...barChartDefaultOptions});

  public financialExpensesChartData: WritableSignal<any> = signal<any>(null);
  public financialExpensesOptions: WritableSignal<ChartConfiguration["options"]> = signal<ChartConfiguration["options"]>({...barChartDefaultOptions});

  public financialInvoicesLoading: WritableSignal<boolean> = signal<boolean>(false);
  public financialInvoicesData: WritableSignal<FinancialInvoice|null> = signal<FinancialInvoice|null>(null);
  public financialInvoicesPaidChartData: WritableSignal<any> = signal<any>(null);
  public financialInvoicesOutgoingChartData: WritableSignal<any> = signal<any>(null);

  public ngOnInit(): void {
    this._initSocket();
  }

  private _initSocket(): void {
    this._socketService.echo
      .channel('dashboard')
      .listen('.updated', (data: { dashboard: DashboardType }) => console.log({data}));
  }

  private _initDashboard(): void {
    this.getFinancialInOut();
    this.getFinancialInOutMetrics();
    this.getFinancialByCategory();
    this.getFinancialInvoices();
    this.getFinancialProfitAndExpenses();
  }

  public ngOnChanges(): void {
    if(!this._isCompletedRangeDates()) {
      return;
    }

    this._initDashboard();
  }

  private _isCompletedRangeDates(): boolean {
    return this.startDateFilter() instanceof Date && this.endDateFilter() instanceof Date;
  }

  public openDialog(template: TemplateRef<any>, title: string): void {
    this.dialogTitle.set(title);
    this.selectedDialogChart.set(template);
    this.dialogVisible = true;
  }

  public closeDialog(): void {
    this.dialogVisible = false;
    this.dialogTitle.set('');
    this.selectedDialogChart.set(null);
  }

  public getFinancialInOut(): void {
    this.financialInOutLoading.set(true);

    const filters = {
      start_date: dayjs(this.endDateFilter()).subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.financialDepositAndWithdrawChart(filters)
      .pipe(finalize(() => this.financialInOutLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.financialInOutData.set(response.data);

            this.mountgetFinancialInOutData();
            this.mountgetFinancialInOutOptions();
          }
        }
      });
  }

  public mountgetFinancialInOutData(): void {
    this.financialInOutChartData.set({
      labels: this.financialInOutData()?.labels,
      datasets: this.financialInOutData()?.series.map((serie: any) => ({
        label: serie.name,
        data: serie.data,
        borderWidth: 1,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
        borderRadius: 6,
        backgroundColor: serie.name === 'Entradas' ? '#4FCF7599' : '#E0332699',
        borderColor: serie.name === 'Entradas' ? '#4FCF7599' : '#E0332699',
      }))
    });
  }

  public mountgetFinancialInOutOptions(): void {
    this.financialInOutOptions.set({
      ...barChartDefaultOptions,
      maintainAspectRatio: false,
      plugins: {
        ...barChartDefaultOptions?.plugins,
        tooltip: {
          ...barChartDefaultOptions?.plugins?.tooltip,
          callbacks: {
            label: function(context: any) {
              return [
                context.dataset.label,
                formatCurrency(Number(context.raw), 'pt_BR', 'R$')
              ];
            }
          }
        }
      },
      scales: {
        ...barChartDefaultOptions?.scales,
        y: {
          grid: {
            display: true,
          },
          ticks: {
            callback: function (value, index, ticks) {
              if (+value >= 1000000) {
                return `${(+value / 1000000)}M`;
              }

              if (+value > 1000) {
                return `${(+value / 1000)}K`;
              }

              return value
            }
          },
          beginAtZero: true
        }
      },
    });
  }

  public getFinancialInOutMetrics(): void {
    this.financialDepositAndWithdrawMetricsLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.financialDepositAndWithdrawMetrics(filters)
      .pipe(finalize(() => this.financialDepositAndWithdrawMetricsLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.financialDepositAndWithdrawMetricsData.set(response.data);
          }
        }
      });
  }

  public getFinancialByCategory(): void {
    this.financialByCategoryLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.financialExpensesByCategory(filters)
      .pipe(finalize(() => this.financialByCategoryLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.financialByCategoryData.set(response.data);

            this.mountFinancialByCategoryData();
            this.mountFinancialByCategoryOptions();
          }
        }
      });
  }

  public mountFinancialByCategoryData(): void {
    const labels = this.financialByCategoryData()?.labels || [];

    this.financialByCategoryChartData.set({
      labels,
      datasets: [
        {
          label: '',
          data: this.financialByCategoryData()?.series,
          borderWidth: 1,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
          borderRadius: 6,
          backgroundColor: chartColors.slice(0, labels.length),
          borderColor: chartColors.slice(0, labels.length),
        },
      ]
    });
  }

  public mountFinancialByCategoryOptions(): void {
    this.financialByCategoryOptions.set({
      ...barChartDefaultOptions,
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        ...barChartDefaultOptions?.scales,
        y: {
          ticks: {
            callback: function (value, index, ticks) {
              if (+value >= 1000000) {
                return `${(+value / 1000000)}M`;
              }

              if (+value >= 1000) {
                return `${(+value / 1000)}K`;
              }

              return `${value}`
            }
          },
        }
      },
      plugins: {
        ...barChartDefaultOptions?.plugins,
        legend: {
          display: false
        }
      }
    })
  }

  public getFinancialInvoices(): void {
    this.financialInvoicesLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.financialInvoices(filters)
      .pipe(finalize(() => this.financialInvoicesLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.financialInvoicesData.set(response.data);

            this.mountfinancialInvoicesData();
          }
        }
      });
  }

  public mountfinancialInvoicesData(): void {
    this.financialInvoicesPaidChartData.set({
      dataset: {
        data: [
          this.financialInvoicesData()?.paid?.deposited || 0,
          this.financialInvoicesData()?.paid?.not_deposited || 0
        ],
        backgroundColor: ['bg-emerald-600', 'bg-emerald-300']
      }
    });

    this.financialInvoicesOutgoingChartData.set({
      dataset: {
        data: [
          this.financialInvoicesData()?.outstanding?.in_day || 0,
          this.financialInvoicesData()?.outstanding?.overdue || 0
        ],
        backgroundColor: ['bg-rose-600', 'bg-rose-300']
      }
    });
  }

  public getFinancialProfitAndExpenses(): void {
    this.financialProfitExpensesLoading.set(true);

    const filters = {
      start_date: dayjs(this.startDateFilter()).format('YYYY-MM-DD'),
      end_date: dayjs(this.endDateFilter()).format('YYYY-MM-DD')
    };

    if (!filters.start_date || !filters.end_date) {
      return;
    }

    this._dashboardService.financialProfitAndExpenses(filters)
      .pipe(finalize(() => this.financialProfitExpensesLoading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.financialProfitExpensesData.set(response.data);

            this.mountFinancialProfitData();
            this.mountFinancialProfitOptions();

            this.mountFinancialExpensesData();
            this.mountFinancialExpensesOptions();
          }
        }
      })
  }

  public mountFinancialProfitData(): void {
    this.financialProfitChartData.set({
      labels: ['Receitas'],
      datasets: [
        {
          label: 'Pago',
          data: [this.financialProfitExpensesData()?.receita_bruta.pagas.valor],
          borderWidth: 1,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
          borderRadius: 8,
          backgroundColor: '#4FCF7599',
          borderColor: '#4FCF75FF',
        }
      ]
    });
  }

  public mountFinancialProfitOptions(): void {
    this.financialProfitOptions.set({
      ...barChartDefaultOptions,
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          border: { display: false },
          ticks: { display: false },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { display: false },
          border: { display: false }
        },
      },
      plugins: {
        ...barChartDefaultOptions?.plugins,
        legend: { display: false }
      },
    })
  }

  public mountFinancialExpensesData(): void  {
    this.financialExpensesChartData.set({
      labels: ['Despesas'],
      datasets: [
        {
          label: 'Pago',
          data: [this.financialProfitExpensesData()?.despesas.pagas.valor],
          borderWidth: 1,
          barPercentage: 0.6,
          categoryPercentage: 0.7,
          borderRadius: 8,
          backgroundColor: '#E0332699',
          borderColor: '#E03326FF',
        }
      ]
    });
  }

  public mountFinancialExpensesOptions(): void  {
    this.financialExpensesOptions.set({
      ...barChartDefaultOptions,
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          border: { display: false },
          ticks: { display: false },
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { display: false },
          border: { display: false }
        },
      },
      plugins: {
        ...barChartDefaultOptions?.plugins,
        legend: { display: false }
      },
    })
  }
}
